import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Quote, Star, Check, X, Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';


export default function Testimonials() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const isLoggedIn = localStorage.getItem('user_authenticated') === 'true' || localStorage.getItem('admin_authenticated') === 'true';


  const [storiesList, setStoriesList] = useState([]);
  const [loadingStories, setLoadingStories] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [toast, setToast] = useState(null);
  const [likedStories, setLikedStories] = useState({});

  useEffect(() => {
    if (selectedStory) {
      document.body.style.overflow = 'hidden';
      if (window.lenis) window.lenis.stop();
    } else {
      document.body.style.overflow = 'auto';
      if (window.lenis) window.lenis.start();
    }
    return () => {
      document.body.style.overflow = 'auto';
      if (window.lenis) window.lenis.start();
    };
  }, [selectedStory]);

  const loggedInName = localStorage.getItem('user_name') || localStorage.getItem('admin_name') || '';
  const loggedInEmail = localStorage.getItem('user_email') || localStorage.getItem('admin_email') || '';

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const [form, setForm] = useState({
    author: loggedInName,
    role: '',
    origin: '',
    contact: loggedInEmail,
    journey: '',
    quote: ''
  });
  const [imagePreview, setImagePreview] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const fetchStories = async () => {
    setLoadingStories(true);
    try {
      const res = await fetch(`${apiBase}/api/stories`);
      if (res.status === 429) { 

        setToast({ message: 'Rate limit exceeded. Please wait a minute.', type: 'error' });

        return;

      }

      if (res.ok) {
        const dbStories = await res.json();
        setStoriesList(dbStories);
      }
    } catch (error) {
      console.error("Error fetching stories from backend:", error);
    } finally {
      setLoadingStories(false);
    }
  };

  useEffect(() => {
    fetchStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showToast("Image size must be less than 2MB.", "error");
        e.target.value = "";
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLoggedIn) {
      showToast("Please sign in to share your story.", "error");
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    // Derive initials
    const words = form.author ? form.author.split(' ').filter(Boolean) : [];
    const initials = words.map(w => w[0]).filter(Boolean).join('').substring(0, 2).toUpperCase();


    // Create random gradient for initials fallback
    const gradients = [
      "linear-gradient(135deg, #8a5a36 0%, #d4af37 100%)",
      "linear-gradient(135deg, #3d5a45 0%, #c5a059 100%)",
      "linear-gradient(135deg, #a64b2a 0%, #d4af37 100%)",
      "linear-gradient(135deg, #706152 0%, #b58925 100%)"
    ];
    const randomBg = gradients[Math.floor(Math.random() * gradients.length)];

    if (!rating || rating < 1) {
      showToast("Please select a star rating for your experience.", "error");
      return;
    }

    const newStory = {
      quote: form.quote,
      author: form.author,
      role: form.role,
      origin: form.origin,
      journey: form.journey,
      contact: form.contact,
      avatarBg: randomBg,
      initials: initials || "SK",
      image: imagePreview,
      rating: rating
    };

    try {
      const res = await fetch(`${apiBase}/api/stories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newStory)
      });
      if (res.status === 429) { 

        setToast({ message: 'Rate limit exceeded. Please wait a minute.', type: 'error' });

        return;

      }

      if (res.ok) {
        // Show success feedback
        showToast("Thank you! Your story has been submitted and is pending administrator review.");

        // Reset Form
        setForm({
          author: loggedInName,
          role: '',
          origin: '',
          contact: loggedInEmail,
          journey: '',
          quote: ''
        });
        setRating(0);
        setHoverRating(0);
        setImagePreview(null);
      } else {
        const errData = await res.json();
        let errMsg = "Failed to share story.";
        if (errData && errData.detail) {
          if (typeof errData.detail === 'string') {
            errMsg = errData.detail;
          } else if (Array.isArray(errData.detail)) {
            errMsg = errData.detail.map(err => err.msg).join(", ");
          } else if (typeof errData.detail === 'object') {
            errMsg = JSON.stringify(errData.detail);
          }
        }
        showToast(errMsg, "error");
      }
    } catch (err) {
      showToast("Backend service is offline. Could not submit story.", "error");
    }
  };

  const renderStoryCard = (story, index) => {
    const truncatedQuote = story.quote.length > 110 ? story.quote.substring(0, 110) + '...' : story.quote;
    return (
      <div
        key={index}
        onClick={() => setSelectedStory(story)}
        className="glass-card story-frame-hover"
        style={{
          padding: '28px 24px',
          borderRadius: '16px',
          position: 'relative',
          boxShadow: '0 8px 32px rgba(41, 33, 26, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          transition: 'var(--transition-smooth)',
          border: '1px solid var(--primary-gold-border)',
          cursor: 'pointer',
          minHeight: '250px'
        }}
      >
        <div style={{ position: 'absolute', top: '15px', left: '20px', color: 'rgba(212, 175, 55, 0.05)', zIndex: 1, pointerEvents: 'none' }}>
          <Quote size={50} strokeWidth={1} />
        </div>

        <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
              {[1, 2, 3, 4, 5].map((star) => {
                const filled = story.rating ? star <= story.rating : true;
                return (
                  <Star key={star} size={12} fill={filled ? 'var(--primary-gold)' : 'transparent'} stroke={filled ? 'var(--primary-gold)' : 'rgba(181, 137, 37, 0.35)'} strokeWidth={1.5} />
                );
              })}
            </div>
            <p className="font-serif" style={{ fontSize: '0.92rem', lineHeight: '1.5', color: 'var(--text-light)', fontStyle: 'italic', marginBottom: '15px' }}>
              "{truncatedQuote}"
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderTop: '1px solid rgba(181, 137, 37, 0.08)', paddingTop: '12px' }}>
            {story.image ? (
              <img src={story.image} alt={story.author} style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--primary-gold-border)', boxShadow: '0 2px 6px rgba(41, 33, 26, 0.1)', flexShrink: 0 }} />
            ) : (
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: story.avatarBg, border: '1px solid var(--primary-gold-border)', boxShadow: '0 2px 6px rgba(41, 33, 26, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dark)', fontWeight: '600', fontSize: '0.85rem', letterSpacing: '0.05em', flexShrink: 0 }}>
                {story.initials}
              </div>
            )}
            <div style={{ textAlign: 'left', minWidth: 0 }}>
              <h4 style={{ fontSize: '0.85rem', color: 'var(--primary-gold)', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{story.author}</h4>
              <p style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '1px', textTransform: 'uppercase', letterSpacing: '0.05em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{story.role} • {story.origin}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section
      id="testimonials"
      style={{
        padding: '100px 0',
        backgroundColor: 'var(--bg-deep)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Toast Alert */}
      {toast && (
        <div
          className="animate-fade-in"
          style={{
            position: 'fixed',
            top: '90px',
            right: '30px',
            zIndex: 9999,
            backgroundColor: toast.type === 'error' ? 'var(--accent-terracotta)' : 'var(--accent-green)',
            color: '#fff',
            padding: '14px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        >
          {toast.type === 'error' ? <X size={18} /> : <Check size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Background Graphic */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(212, 175, 55, 0.02) 0%, transparent 60%)',
          zIndex: 1,
          pointerEvents: 'none'
        }}
      />

      <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 2, padding: '0 20px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <span
            className="text-saffron"
            style={{
              textTransform: 'uppercase',
              letterSpacing: '0.3em',
              fontSize: '0.8rem',
              fontWeight: '600'
            }}
          >
            {t('testimonials.header', { defaultValue: 'Contemplative Stories' })}
          </span>
          <h2
            className="font-serif"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              marginTop: '10px',
              color: 'var(--text-light)'
            }}
          >
            {t('testimonials.title', { defaultValue: 'Customer Stories' })}
          </h2>
          <div
            style={{
              width: '60px',
              height: '2px',
              backgroundColor: 'var(--primary-saffron)',
              margin: '20px auto 0'
            }}
          />
        </div>

        {/* Main Content Layout */}
        <div style={{ marginBottom: '60px' }}>
          {loadingStories ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              Loading stories…
            </div>
          ) : storiesList.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: 'var(--text-muted)'
            }}>
              <p style={{ fontSize: '2rem', marginBottom: '12px' }}>🙏</p>
              <p style={{ fontSize: '1rem', fontWeight: '500', color: 'var(--text-light)', marginBottom: '8px' }}>No stories yet</p>
              <p style={{ fontSize: '0.85rem' }}>Be the first to share your spiritual journey with the community.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '30px' }}>
              {storiesList.map((story, i) => renderStoryCard(story, i))}
            </div>
          )}
        </div>

        {/* Selected Story Detail Modal */}
        {selectedStory && createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(7, 5, 4, 0.45)',
              backdropFilter: 'blur(5px)',
              zIndex: 99999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={() => setSelectedStory(null)}
          >
            <div
              className="glass-card animate-fade-in"
              data-lenis-prevent
              style={{
                width: '100%',
                maxWidth: '540px',
                padding: '30px 25px',
                position: 'relative',
                maxHeight: '90vh',
                overflowY: 'auto',
                border: '1px solid var(--primary-gold-border)',
                borderRadius: '16px',
                boxShadow: '0 12px 40px rgba(41, 33, 26, 0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedStory(null)}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '5px'
                }}
                title="Close"
              >
                <X size={20} />
              </button>

              <span style={{ fontSize: '0.72rem', letterSpacing: '0.12em', fontWeight: 'bold', color: 'var(--primary-gold)', textTransform: 'uppercase' }}>
                {t('testimonials.modalHeader', { defaultValue: 'Seeker Testimony' })}
              </span>
              <h3 className="font-serif text-gradient" style={{ fontSize: '1.6rem', marginTop: '5px', marginBottom: '20px' }}>
                {t('testimonials.modalBy', { author: selectedStory.author, defaultValue: `By: ${selectedStory.author}` })}
              </h3>

              {selectedStory.image ? (
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '25px', paddingBottom: '15px', borderBottom: '1px solid rgba(181, 137, 37, 0.12)' }}>
                  <img
                    src={selectedStory.image}
                    alt={selectedStory.author}
                    style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2.5px solid var(--primary-gold)' }}
                  />
                  <div>
                    <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: 'var(--text-light)' }}>
                      {selectedStory.author}
                    </h4>
                    <p style={{ margin: '1px 0 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      {selectedStory.role} from {selectedStory.origin}
                    </p>
                    <span style={{ display: 'inline-block', fontSize: '0.65rem', color: 'var(--primary-saffron)', backgroundColor: 'rgba(194, 94, 26, 0.08)', padding: '1px 8px', borderRadius: '10px', marginTop: '4px', fontWeight: '600', letterSpacing: '0.03em' }}>
                      {t('testimonials.modalJourney', { journey: selectedStory.journey, defaultValue: `Journey: ${selectedStory.journey}` })}
                    </span>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '25px', paddingBottom: '15px', borderBottom: '1px solid rgba(181, 137, 37, 0.12)' }}>
                  {!selectedStory.image && (
                    <div
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: selectedStory.avatarBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-dark)',
                        fontSize: '1.4rem',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                      }}
                    >
                      {selectedStory.initials}
                    </div>
                  )}
                  <div>
                    <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: 'var(--text-light)' }}>
                      {selectedStory.author}
                    </h4>
                    <p style={{ margin: '1px 0 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      {selectedStory.role} from {selectedStory.origin}
                    </p>
                    <span style={{ display: 'inline-block', fontSize: '0.65rem', color: 'var(--primary-saffron)', backgroundColor: 'rgba(194, 94, 26, 0.08)', padding: '1px 8px', borderRadius: '10px', marginTop: '4px', fontWeight: '600', letterSpacing: '0.03em' }}>
                      {t('testimonials.modalJourney', { journey: selectedStory.journey, defaultValue: `Journey: ${selectedStory.journey}` })}
                    </span>
                  </div>
                </div>
              )}

              {selectedStory.image && (
                <div style={{ marginBottom: '25px', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                  <img
                    src={selectedStory.image}
                    alt="Journey"
                    style={{ width: '100%', maxHeight: '350px', objectFit: 'cover', display: 'block' }}
                  />
                </div>
              )}

              <p style={{ fontSize: '0.95rem', fontStyle: 'italic', color: 'var(--text-light)', lineHeight: '1.7', marginBottom: '25px', whiteSpace: 'pre-line' }}>
                "{selectedStory.quote}"
              </p>

              {selectedStory.rating && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', padding: '12px 18px', backgroundColor: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.15)', borderRadius: '10px' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('testimonials.modalRating', { defaultValue: 'Experience Rating' })}</span>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={14}
                        style={{ marginRight: '2px' }}
                        fill={star <= selectedStory.rating ? 'var(--primary-gold)' : 'transparent'}
                        stroke={star <= selectedStory.rating ? 'var(--primary-gold)' : 'rgba(181, 137, 37, 0.3)'}
                      />
                    ))}
                    <span style={{ marginLeft: '6px', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: '600' }}>
                      {selectedStory.rating}/5
                    </span>
                  </div>
                </div>
              )}

              {/* Actions Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid rgba(181, 137, 37, 0.1)', paddingTop: '15px' }}>
                <button
                  onClick={() => setLikedStories(prev => ({ ...prev, [selectedStory.id]: !prev[selectedStory.id] }))}
                  className="btn-gold"
                  style={{ padding: '8px 24px', borderRadius: '20px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Heart
                    size={16}
                    fill={likedStories[selectedStory.id] ? 'var(--text-dark)' : 'transparent'}
                    stroke={likedStories[selectedStory.id] ? 'var(--text-dark)' : 'currentColor'}
                  />
                  {likedStories[selectedStory.id] ? t('testimonials.btnLiked', { defaultValue: 'Liked' }) : t('testimonials.btnLike', { defaultValue: 'Like' })}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Share Your Story Form Card */}
        <div
          className="glass-card share-story-card"
          style={{
            marginTop: '80px',
            maxWidth: '800px',
            margin: '80px auto 0',
            padding: '40px 35px',
            borderRadius: '16px',
            boxShadow: '0 8px 32px 0 rgba(41, 33, 26, 0.08)',
            border: '1px solid rgba(181, 137, 37, 0.25)'
          }}
        >
          {!isLoggedIn ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <h3 className="font-serif text-gold" style={{ fontSize: '1.6rem', marginBottom: '12px' }}>
                {t('testimonials.shareHeader', { defaultValue: 'Share Your Transformation' })}
              </h3>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', maxWidth: '600px', margin: '0 auto 25px', lineHeight: '1.6' }}>
                {t('testimonials.shareDesc', { defaultValue: 'Join our circle of seekers. Share your personal journey, spiritual realizations, and photographs of your sacred travels in Bharat to inspire others.' })}
              </p>
              <button
                onClick={() => navigate('/login', { state: { from: location.pathname } })}
                className="btn-gold pulse-gold-button"
                style={{
                  padding: '14px 40px',
                  borderRadius: '30px',
                  fontSize: '0.85rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {t('testimonials.btnSignIn', { defaultValue: 'Sign In to Share' })}
              </button>
            </div>
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <h3 className="font-serif text-gold" style={{ fontSize: '1.6rem', marginBottom: '8px' }}>
                  {t('testimonials.shareTitle', { defaultValue: 'Share Your Journey' })}
                </h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
                  {t('testimonials.shareSubtitle', { defaultValue: 'Has your spirit found alignment in Bharat? Inspire other seekers by sharing your transformation story.' })}
                </p>
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>

                  {/* Name */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600' }}>{t('testimonials.labelName', { defaultValue: 'Full Name' })}</label>
                    <input
                      type="text"
                      name="author"
                      value={form.author}
                      onChange={handleInputChange}
                      required
                      placeholder={t("testimonials.phName", { defaultValue: "e.g. John Doe" })}
                      style={{
                        padding: '12px 16px',
                        background: 'rgba(255, 255, 255, 0.75)',
                        border: '1px solid rgba(181, 137, 37, 0.25)',
                        borderRadius: '30px',
                        color: 'var(--text-light)',
                        fontSize: '0.85rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Role */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600' }}>{t('testimonials.labelRole', { defaultValue: 'Traveler Role' })}</label>
                    <input
                      type="text"
                      name="role"
                      value={form.role}
                      onChange={handleInputChange}
                      required
                      placeholder={t("testimonials.phRole", { defaultValue: "e.g. Solo, Family, Couple" })}
                      style={{
                        padding: '12px 16px',
                        background: 'rgba(255, 255, 255, 0.75)',
                        border: '1px solid rgba(181, 137, 37, 0.25)',
                        borderRadius: '30px',
                        color: 'var(--text-light)',
                        fontSize: '0.85rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Origin */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600' }}>{t('testimonials.labelOrigin', { defaultValue: 'Location / City' })}</label>
                    <input
                      type="text"
                      name="origin"
                      value={form.origin}
                      onChange={handleInputChange}
                      required
                      placeholder={t("testimonials.phOrigin", { defaultValue: "e.g. New York, USA" })}
                      style={{
                        padding: '12px 16px',
                        background: 'rgba(255, 255, 255, 0.75)',
                        border: '1px solid rgba(181, 137, 37, 0.25)',
                        borderRadius: '30px',
                        color: 'var(--text-light)',
                        fontSize: '0.85rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Contact */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600' }}>{t('testimonials.labelContact', { defaultValue: 'Contact (Email or Phone)' })}</label>
                    <input
                      type="text"
                      name="contact"
                      value={form.contact}
                      onChange={handleInputChange}
                      required
                      placeholder={t("testimonials.phContact", { defaultValue: "e.g. seeker@email.com" })}
                      style={{
                        padding: '12px 16px',
                        background: 'rgba(255, 255, 255, 0.75)',
                        border: '1px solid rgba(181, 137, 37, 0.25)',
                        borderRadius: '30px',
                        color: 'var(--text-light)',
                        fontSize: '0.85rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Journey Taken */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600' }}>{t('testimonials.labelJourney', { defaultValue: 'Pilgrimage / Journey Taken' })}</label>
                    <input
                      type="text"
                      name="journey"
                      value={form.journey}
                      onChange={handleInputChange}
                      required
                      placeholder={t("testimonials.phJourney", { defaultValue: "e.g. Himalayan Meditation Sadhana" })}
                      style={{
                        padding: '12px 16px',
                        background: 'rgba(255, 255, 255, 0.75)',
                        border: '1px solid rgba(181, 137, 37, 0.25)',
                        borderRadius: '30px',
                        color: 'var(--text-light)',
                        fontSize: '0.85rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Image Upload */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600' }}>{t('testimonials.labelPhoto', { defaultValue: 'Upload Photograph' })}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '6px' }}>
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary-gold)' }} />
                      ) : (
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(181, 137, 37, 0.08)', border: '1px dashed var(--primary-gold-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary-gold)', fontSize: '0.65rem', fontWeight: '500' }}>Photo</div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ fontSize: '0.8rem', color: 'var(--text-muted)', outline: 'none' }}
                      />
                    </div>
                  </div>

                </div>

                {/* Description */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600' }}>{t('testimonials.labelDesc', { defaultValue: 'Your Spiritual Experience (Description)' })}</label>
                  <textarea
                    name="quote"
                    value={form.quote}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    placeholder={t("testimonials.phDesc", { defaultValue: "Describe your transformation, insights, or experiences during the journey..." })}
                    style={{
                      padding: '12px 18px',
                      background: 'rgba(255, 255, 255, 0.75)',
                      border: '1px solid rgba(181, 137, 37, 0.25)',
                      borderRadius: '16px',
                      color: 'var(--text-light)',
                      fontSize: '0.85rem',
                      outline: 'none',
                      resize: 'none',
                      lineHeight: '1.5'
                    }}
                  />
                </div>

                {/* Star Rating Selector */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600' }}>
                    Rate Your Experience <span style={{ color: 'var(--accent-terracotta)' }}>*</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          transition: 'transform 0.15s ease',
                          transform: (hoverRating || rating) >= star ? 'scale(1.2)' : 'scale(1)'
                        }}
                        title={`Rate ${star} out of 5`}
                      >
                        <Star
                          size={28}
                          fill={(hoverRating || rating) >= star ? 'var(--primary-gold)' : 'transparent'}
                          stroke={(hoverRating || rating) >= star ? 'var(--primary-gold)' : 'rgba(181, 137, 37, 0.4)'}
                          strokeWidth={1.5}
                          style={{ display: 'block', filter: (hoverRating || rating) >= star ? 'drop-shadow(0 0 4px rgba(212,175,55,0.5))' : 'none' }}
                        />
                      </button>
                    ))}
                    {rating > 0 && (
                      <span style={{ marginLeft: '8px', fontSize: '0.85rem', color: 'var(--primary-gold)', fontWeight: '600' }}>
                        {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent!'][rating]}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-gold pulse-gold-button"
                  style={{
                    alignSelf: 'center',
                    padding: '14px 40px',
                    borderRadius: '30px',
                    fontSize: '0.85rem',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    border: 'none',
                    cursor: 'pointer',
                    marginTop: '10px'
                  }}
                >
                  Share Your Story
                </button>
              </form>
            </>
          )}
        </div>

      </div>
      <style>{`
        .story-frame-hover {
          background: rgba(255, 255, 255, 0.45) !important;
          border: 1px solid rgba(181, 137, 37, 0.15) !important;
          border-radius: 12px;
          transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
          position: relative;
          overflow: hidden;
        }
        .story-frame-hover:hover {
          transform: translateY(-8px) scale(1.01);
          background: rgba(255, 255, 255, 0.85) !important;
          border-color: rgba(212, 175, 55, 0.5) !important;
          box-shadow: 0 20px 45px rgba(181, 137, 37, 0.12) !important;
        }
        .share-story-card {
          background: rgba(255, 255, 255, 0.8) !important;
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(181, 137, 37, 0.2) !important;
          border-radius: 16px;
        }
        .share-story-card input,
        .share-story-card textarea {
          box-sizing: border-box;
          width: 100%;
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
        }
        .share-story-card input:focus,
        .share-story-card textarea:focus {
          border-color: var(--primary-gold) !important;
          box-shadow: 0 0 0 3px rgba(181, 137, 37, 0.15), 0 4px 10px rgba(181, 137, 37, 0.05) !important;
          background: #ffffff !important;
        }
        @media (max-width: 768px) {
          /* Section padding */
          #testimonials {
            padding: 60px 0 40px !important;
          }

          /* Header */
          #testimonials h2 {
            font-size: clamp(1.6rem, 6vw, 2.2rem) !important;
          }

          /* Story cards grid → 1 column */
          .testimonials-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }

          /* Story card padding */
          .story-frame-hover {
            padding: 18px !important;
          }

          /* Modal inner */
          .testimonial-modal-inner {
            padding: 20px 16px !important;
            max-height: 90dvh !important;
            max-width: 100% !important;
            border-radius: 12px !important;
          }

          /* Share story form card */
          .share-story-card {
            padding: 24px 16px !important;
            margin: 40px auto 0 !important;
          }

          /* Form grid → single column */
          .share-story-card form > div[style*='grid'] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}
